import * as React from 'react'
import { Modal } from 'antd'
import bind from 'bind-decorator'
import { API, graphqlOperation } from 'aws-amplify'
import moment from 'moment'
import { loadPopupDetails } from '../../../services/Api'
import { PopupCard, awsToPhone, DictionaryValue, phoneToAws } from '../../../components'
import EventType from '../../../models/eventType'
import './Popup.scss'
import { getCurrentUser, getAttributeByName, handleError } from '../../../utilities'
import { CognitoUser } from '@aws-amplify/auth'
import { CognitoUserAttribute } from 'amazon-cognito-identity-js'
import { createUserPopupAnswers } from '../../../graphql/mutations'
import { GraphQLResult } from '@aws-amplify/api/lib/types'
import { setCookie, getCookie } from '../../../utilities/cookies'
import { AddressFormat } from '../../account/types'
import { history } from '../../../history'
import { renderAllData } from '../../../utilities/graphql'

type PopupListing = {
    id: string
    start: number
    end: number
    finish: number
}

type PopupDetail = {
    id: string
    title: string
    start: number
    end: number
    finish: number
    product: string
    price: number
    rrp: number
    img: string
    question: string
}

export type FieldProps = {
    loading: boolean
    isLoggedIn: boolean
    popups: string | null
    eventType?: EventType
}

export type MethodProps = {
    loadPopups: () => void
}

type Props = FieldProps & MethodProps

type State = {
    attributes: CognitoUserAttribute[]
    show: boolean
    answerSubmitted: boolean
    popup?: PopupDetail
    noAddress?: boolean
    noAddressModalShow?: boolean
}

class Popup extends React.Component<Props, State> {
    readonly state: State = { attributes: [], show: false, answerSubmitted: false, popup: undefined }

    async componentDidMount() {
        this.props.loadPopups()
        this.prepareUser()
        this.checkPopup()

        history.listen((location, action) => {
            if ((location.pathname).toLowerCase() === '/terms-and-conditions' &&
                (location.search).toLowerCase() === '?popup=true'
            ) {
                this.setState({ show: false })
            } else {
                this.props.loadPopups()
                this.prepareUser()
                this.checkPopup()
            }
        })
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.popups !== this.props.popups && this.props.popups !== null) {
            this.checkPopup()
        }
    }

    @bind
    async prepareUser() {
        const user = await getCurrentUser()
        const attributes = await new Promise<CognitoUserAttribute[]>((resolve, reject) => {
            (user as CognitoUser).getUserAttributes((err, attributes) => {
                if (err) {
                    reject(err)
                    return
                }

                resolve(attributes)
            })
        })
        this.setState({ attributes })

        const fullAddress = getAttributeByName('address', attributes)
        if (!fullAddress) {
            this.setState({ noAddress: true })
        }
    }

    @bind
    async checkPopup() {
        if (this.props.popups) {
            try {
                let result = {
                    response: [],
                    success: false,
                }

                result = await this.decryptCall(this.props.popups)

                let activePopupId = null

                if (result.response !== undefined) {
                    const popups = result.response as PopupListing[]
                    popups.forEach((popup: PopupListing) => {
                        const now = Math.floor(Date.now() / 1000)
                        const startTimeout = popup.start - now
                        const finishTimeout = popup.finish - now

                        if (startTimeout > 0) {
                            setTimeout(() => { this.openPopup(popup.id) }, startTimeout * 1000)
                        }

                        if (finishTimeout > 0) {
                            setTimeout(() => { this.closePopup() }, finishTimeout * 1000)
                        }

                        if (now >= Number(popup.start) && now <= Number(popup.end)) {
                            activePopupId = popup.id
                        }
                    })
                }

                if (activePopupId) {
                    this.openPopup(activePopupId)
                }

            } catch (e) {
                console.log('Error: ', e)
            }
        }
    }

    @bind
    async openPopup(activePopupId: string) {
        const { noAddress } = this.state

        if (getCookie('popupClosed')) {
            return
        }

        if (noAddress) {
            if (!getCookie('noAddressReminderClosed')) {
                this.setState({ noAddressModalShow: true })
            }
            return
        }

        // let duplicateCheck = false
        // duplicateCheck = await this.checkDuplicateEntry(activePopupId)

        // if (!duplicateCheck) {
        //     return
        // }

        const popupDetail = await loadPopupDetails(activePopupId)
        let result = {
            response: {},
            success: false,
        }

        result = await this.decryptCall(popupDetail, Math.floor(Date.now() / 1000))

        if (result.success) {
            this.setState({ show: true, popup: result.response as PopupDetail })
        }
    }

    @bind
    closePopup() {
        const { popup } = this.state
        this.setState({
            show: false,
            popup: undefined,
        })

        const now = Math.floor(Date.now() / 1000)
        if (popup) {
            const expireTime = (popup.end - now) * 1000
            setCookie('popupClosed', true, expireTime)
        }
    }

    @bind
    onCancel(e: React.MouseEvent) {
        e.preventDefault()
        this.closePopup()
    }

    @bind
    async handleSubmit(answer: string, mobile: string) {
        const { attributes, popup } = this.state
        const fullAddress = getAttributeByName('address', attributes)
        let address: AddressFormat | undefined = undefined
        if (fullAddress) {
            address = JSON.parse(fullAddress)
        } else {
            handleError('Invalid Address')
        }

        API.graphql(
            graphqlOperation(createUserPopupAnswers, {
                input:{
                    answer,
                    popupId: popup && popup.id,
                    username: getAttributeByName('sub', attributes),
                    firstName: getAttributeByName('given_name', attributes),
                    lastName: getAttributeByName('family_name', attributes),
                    email: getAttributeByName('email', attributes),
                    mobile: phoneToAws(mobile),
                    addressline1: address && address.street_address,
                    addressline2: (address && address.address_2 !== '') ? address.address_2 : undefined,
                    suburb: address && address.locality,
                    state: address && address.region,
                    postcode: address && address.postal_code,
                    product: popup && popup.product,
                    question: popup && popup.question,
                    answeredAt: moment(),
                },
            })
        ).then((response: GraphQLResult) => {
            if (response.data && response.data !== null) {
                this.setState({ answerSubmitted: true })
                setTimeout(() => {
                    this.closePopup()
                    this.setState({ answerSubmitted: false })
                },         15000)
            } else {
                handleError(response.errors)
            }
        })
    }

    @bind
    async checkDuplicateEntry(activePopupId: string) {
        const { attributes } = this.state
        const username = getAttributeByName('sub', attributes)

        if (username) {
            const filter = {
                popupId: { eq: activePopupId },
                username: { eq:  username },
            }

            const answers = await renderAllData(
                [], undefined, 'listUserPopupAnswerssBasic', filter, 'listUserPopupAnswerss'
            )

            if (answers.length === 0) {
                return true
            }
        }
        return false
    }

    @bind
    handleNoAddressOk(e: React.MouseEvent) {
        e.preventDefault()
        const { popup } = this.state
        const now = Math.floor(Date.now() / 1000)
        let expireTime = 86400 * 1000
        if (popup) {
            expireTime = (popup.end - now) * 1000
        }
        setCookie('noAddressReminderClosed', true, expireTime)
        this.setState({
            noAddressModalShow: false,
        })
        history.replace('/account')
    }

    @bind
    handleNoAddressCancel(e: React.MouseEvent) {
        e.preventDefault()
        this.setState({
            noAddressModalShow: false,
        })
        const { popup } = this.state
        const now = Math.floor(Date.now() / 1000)
        if (popup) {
            const expireTime = (popup.end - now) * 1000
            setCookie('noAddressReminderClosed', true, expireTime)
        } else {
            setCookie('noAddressReminderClosed', true, 86400 * 1000)
        }
    }

    @bind
    decryptCall(data: any, time?: number) {
        return (API.post('clickfrenzyFeDecrypt', '/key', { body: {
            data,
            time,
        }}).catch((err) => {
            setTimeout(() => {
                this.decryptCall(data)
            },         1000)
        }))
    }

    render() {
        const { loading, isLoggedIn, eventType } = this.props
        const { attributes, show, answerSubmitted, popup, noAddress, noAddressModalShow } = this.state

        const mobile = getAttributeByName('phone_number', attributes)

        return (
        <div className='cfa-popup'>
            {(popup && eventType && !noAddress) &&
                <Modal
                    centered={true}
                    visible={show && !loading && isLoggedIn}
                    onCancel={this.onCancel}
                    className={`cfa-popup__modal cfa-popup__modal--${eventType}`}
                    width={800}
                    footer={[]}
                >
                    <PopupCard
                        type={eventType}
                        popupTitle={popup.title}
                        productName={popup.product}
                        productImage={popup.img}
                        productPrice={popup.price}
                        productRRP={popup.rrp}
                        popupQuestion={popup.question}
                        popupTime={popup.finish}
                        handleSubmit={this.handleSubmit}
                        answerSubmitted={answerSubmitted}
                        mobile={awsToPhone(mobile)}
                    />
                </Modal>
            }
            {(noAddress && noAddressModalShow) &&
                <Modal
                    centered={true}
                    visible={noAddress && noAddressModalShow && !loading && isLoggedIn}
                    className={`cfa-popup__modal cfa-popup__modal--${eventType} cfa-popup__modal--noAddress`}
                    okText='Edit Profile Now'
                    onOk={this.handleNoAddressOk}
                    onCancel={this.handleNoAddressCancel}
                    okButtonProps={{ size: 'large' }}
                    cancelButtonProps={{ size: 'large' }}
                >
                    <h1><DictionaryValue token={'popup.noaddress.title'} /></h1>
                    <p><DictionaryValue token={'popup.noaddress.content'} /></p>
                </Modal>
            }
        </div>
        )
    }
}

export default Popup
